// Pure decisions behind every "Grupos" section: the Tablero's Asesor filter and
// the asesor picker on assignment rules. A group is a saved preset, never its
// own dimension — selecting one only writes its members into the list the
// caller already works with (`filtros.asesores`, a rule's assignedEmployees).
// Kept out of the components so the rules stay inspectable on their own.

export type FilterGroup = { id: string; label: string; employeeIds: string[] };

export type GroupCheckState = "none" | "some" | "all";

/** A group with no selectable members is always "none" — it can never be satisfied. */
export const groupCheckState = (group: FilterGroup, values: string[]): GroupCheckState => {
  if (!group.employeeIds.length) return "none";
  const hits = group.employeeIds.filter((id) => values.includes(id)).length;
  if (hits === 0) return "none";
  return hits === group.employeeIds.length ? "all" : "some";
};

/**
 * Clicking a fully selected group clears exactly its members and leaves any
 * hand-picked asesor alone; otherwise it adds the ones still missing.
 */
export const toggleGroup = (group: FilterGroup, values: string[]): string[] =>
  groupCheckState(group, values) === "all"
    ? values.filter((id) => !group.employeeIds.includes(id))
    : [...new Set([...values, ...group.employeeIds])];

/**
 * Name the group on the filter pill when the selection is exactly one group's
 * membership — otherwise the caller falls back to "N asesores".
 */
export const matchedGroupLabel = (
  groups: FilterGroup[],
  values: string[]
): string | undefined =>
  groups.find(
    (group) =>
      group.employeeIds.length === values.length && groupCheckState(group, values) === "all"
  )?.label;
